import React, { ChangeEvent } from 'react';

import { Checkbox, Input, Label, Radio, RadioGroup, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { NumberUnitInput } from '~/components/common';

import { useProductModalOptions } from '~/hooks/product';
import { useProductStore, useUserInfoStore } from '~/store';
import { formatNumber, validateSingleProductField } from '~/utils';

import { PRODUCT_LIMITS } from '~/constants';

import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductDetailsModalProductInfoSectionPolicy.module.scss';

const ProductDetailsModalProductInfoSectionPolicy = ({ hasTempData }: { hasTempData: boolean }) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const productModalOptions = useProductModalOptions();

  const [
    productDetailModalValue,
    setProductDetailModalValue,
    productModalRequiredValueStatus,
    setProductModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productDetailModalValue,
      state.setProductDetailModalValue,
      state.productModalRequiredValueStatus,
      state.setProductModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.setFocusFieldRefs,
    ])
  );

  const handleChangeShelfLifeTypeSelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      shelfLifeType: value,
    });

    resetProductModalRequiredValueStatus('shelfLifeType');
  };

  const handleClickRequiredInput = (id: string) => {
    resetProductModalRequiredValueStatus(id as keyof TypeProductModalRequiredInputState);
  };

  const handleChangeDaysInput = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.currentTarget.id as keyof TypeProductModalRequiredInputState;
    const value = e.target.value === '' ? null : e.target.value;

    setProductDetailModalValue({
      ...productDetailModalValue,
      [id]: value,
    });

    const validationResult = validateSingleProductField(id, value);

    setProductModalRequiredValueStatus({
      ...productModalRequiredValueStatus,
      [id]: validationResult,
    });
  };

  const handleChangeKeepLevelSelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      temperatureType: value,
    });

    resetProductModalRequiredValueStatus('temperatureType');
  };

  const handleChangeIsOneDayProductRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      isOneDayProduct: e.target.value === 'true',
    });
  };

  const handleChangeCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      [e.target.id]: e.currentTarget.checked,
    });
  };

  const handleChangeRefrigerantRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      isNeedRefrigerant: e.target.value === 'true',
    });

    resetProductModalRequiredValueStatus('isNeedRefrigerant');
  };

  return (
    <>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="기한정책" required />
        <div className={styles['deadline-policy-wrapper']}>
          <Select
            ref={(el) => setFocusFieldRefs('shelfLifeType', el)}
            id="shelfLifeType"
            className={styles['select']}
            size="small"
            options={productModalOptions?.shelfLifeType ?? []}
            value={productDetailModalValue.shelfLifeType}
            disabled
            status={productModalRequiredValueStatus.shelfLifeType.status}
            validationMessage={productModalRequiredValueStatus.shelfLifeType.message}
            onChange={handleChangeShelfLifeTypeSelect}
          />
          <NumberUnitInput
            ref={(el) => setFocusFieldRefs('availableDays', el)}
            id="availableDays"
            disabled
            className={styles['input']}
            value={productDetailModalValue.availableDays ?? ''}
            size="small"
            unit="일"
            placeholder={`${formatNumber(PRODUCT_LIMITS.INPUT_VALUES.POLICY_DAYS)} 이하 숫자만 입력`}
            status={productModalRequiredValueStatus.availableDays.status}
            validationMessage={productModalRequiredValueStatus.availableDays.message}
            onClick={() => handleClickRequiredInput('availableDays')}
            onChange={handleChangeDaysInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="판매가능일수" required />
        <div className={styles['input-wrapper']}>
          <NumberUnitInput
            id="sellAvailableDays"
            ref={(el) => setFocusFieldRefs('sellAvailableDays', el)}
            className={styles['input']}
            disabled
            value={productDetailModalValue.sellAvailableDays ?? ''}
            size="small"
            unit="일"
            placeholder="숫자만 입력"
            status={productModalRequiredValueStatus.sellAvailableDays.status}
            validationMessage={productModalRequiredValueStatus.sellAvailableDays.message}
            onClick={() => handleClickRequiredInput('sellAvailableDays')}
            onChange={handleChangeDaysInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="판매마감일수" required />
        <div className={styles['input-wrapper']}>
          <NumberUnitInput
            id="sellDeadlineDays"
            ref={(el) => setFocusFieldRefs('sellDeadlineDays', el)}
            className={styles['input']}
            disabled
            value={productDetailModalValue.sellDeadlineDays ?? ''}
            size="small"
            unit="일"
            placeholder="숫자만 입력"
            status={productModalRequiredValueStatus.sellDeadlineDays.status}
            validationMessage={productModalRequiredValueStatus.sellDeadlineDays.message}
            onClick={() => handleClickRequiredInput('sellDeadlineDays')}
            onChange={handleChangeDaysInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="보관유형" required />
        <div className={styles['input-wrapper']}>
          <Select
            ref={(el) => setFocusFieldRefs('temperatureType', el)}
            id="temperatureType"
            disabled={hasPermissionFBK || hasTempData}
            size="small"
            options={productModalOptions?.keepLevel ?? []}
            value={productDetailModalValue.temperatureType}
            placeholder="선택"
            status={productModalRequiredValueStatus.temperatureType.status}
            validationMessage={productModalRequiredValueStatus.temperatureType.message}
            onClick={() => handleClickRequiredInput('temperatureType')}
            onChange={handleChangeKeepLevelSelect}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="분류" required />
        <div className={styles['categories-select-wrapper']}>
          <Select
            ref={(el) => setFocusFieldRefs('mainCategoryCode', el)}
            id="mainCategoryCode"
            size="small"
            disabled
            options={[]}
            status={productModalRequiredValueStatus.mainCategoryCode.status}
            validationMessage={productModalRequiredValueStatus.mainCategoryCode.message}
            value={productDetailModalValue.mainCategoryCode}
          />
          <Select
            ref={(el) => setFocusFieldRefs('middleCategoryCode', el)}
            id="middleCategoryCode"
            size="small"
            disabled
            options={[]}
            status={productModalRequiredValueStatus.middleCategoryCode.status}
            validationMessage={productModalRequiredValueStatus.middleCategoryCode.message}
            value={productDetailModalValue.middleCategoryCode}
          />
          <Select
            ref={(el) => setFocusFieldRefs('thirdCategoryCode', el)}
            id="thirdCategoryCode"
            size="small"
            disabled
            options={[]}
            status={productModalRequiredValueStatus.thirdCategoryCode.status}
            validationMessage={productModalRequiredValueStatus.thirdCategoryCode.message}
            value={productDetailModalValue.thirdCategoryCode}
          />
          <Select
            id="fourthCategoryCode"
            size="small"
            disabled
            options={[]}
            value={productDetailModalValue.fourthCategoryCode}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="일판매여부" />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="is-one-day-product"
            value={
              typeof productDetailModalValue.isOneDayProduct === 'boolean'
                ? productDetailModalValue.isOneDayProduct.toString()
                : undefined
            }
            size="medium"
            disabled={hasPermissionFBK || hasTempData}
            onChange={handleChangeIsOneDayProductRadio}
          >
            <Radio value="true" label="예" />
            <Radio value="false" label="아니오" />
          </RadioGroup>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="판매방법" />
        <div className={styles['input-wrapper']}>
          <Input
            className={styles['input']}
            id="sellingType"
            value={
              productModalOptions?.sellingType.find(({ value }) => value === productDetailModalValue.sellingType)
                ?.text ?? ''
            }
            name="sellingType"
            type="text"
            size="small"
            disabled
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="유의성질여부" />
        <div className={styles['caution-checkbox-wrapper']}>
          <Checkbox
            id="isFragile"
            label="유리포함"
            disabled={hasTempData}
            checked={productDetailModalValue.isFragile}
            size="medium"
            onChange={handleChangeCheckbox}
          />
          <Checkbox
            id="isWettable"
            label="눌림주의"
            disabled={hasTempData}
            checked={productDetailModalValue.isWettable}
            size="medium"
            onChange={handleChangeCheckbox}
          />
        </div>
      </div>

      <div ref={(el) => setFocusFieldRefs('isNeedRefrigerant', el)} className={styles['item-row']}>
        <Label className={styles['label']} title="컬리냉매제" required />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="is-need-refrigerant"
            value={
              typeof productDetailModalValue.isNeedRefrigerant === 'boolean'
                ? productDetailModalValue.isNeedRefrigerant.toString()
                : undefined
            }
            size="medium"
            disabled={hasTempData}
            status={productModalRequiredValueStatus.isNeedRefrigerant.status}
            validationMessage={productModalRequiredValueStatus.isNeedRefrigerant.message}
            onChange={handleChangeRefrigerantRadio}
          >
            <Radio value="true" label="냉매제 필요" />
            <Radio value="false" label="냉매제 필요없음" />
          </RadioGroup>
        </div>
      </div>
    </>
  );
};

export default ProductDetailsModalProductInfoSectionPolicy;
